import { Module } from 'vuex'

import { GetChallengeUseCase } from '@/application/problem/getChallengeUseCase'
import { ChallengeLargeClassification } from '@/domain/entities/problem/challengeLargeClassification'
import { ChallengeMiddleClassification } from '@/domain/entities/problem/challengeMiddleClassification'
import { Contest } from '@/domain/entities/problem/contest'
import { ProblemRepository } from '@/domain/repositories/problem/ProblemRepository'
import { DisplayLanguageType } from '@/domain/value_objects/language/displayLanguageType'

interface ChallengeState {
  largeClassificationList: { [id: string]: ChallengeLargeClassification }
  middleClassificationList: { [largeClassificationId: string]: ChallengeMiddleClassification[] }
  contestList: { [largeClassificationId: string]: { [middleClassificationId: string]: Contest[] } }
  isLoading: boolean
  error: string | null
}

export const challengeModule = (problemRepository: ProblemRepository): Module<ChallengeState, any> => {
  const getChallengeUseCase = new GetChallengeUseCase(problemRepository)

  return {
    namespaced: true,
    state: {
      largeClassificationList: {},
      middleClassificationList: {},
      contestList: {},
      isLoading: false,
      error: null
    },
    mutations: {
      setLargeClassificationList(
        state,
        { largeClassificationList }: { largeClassificationList: ChallengeLargeClassification[] }
      ) {
        const newList = largeClassificationList.reduce(
          (acc, classification) => ({ ...acc, [classification.id]: classification }),
          {} as { [id: string]: ChallengeLargeClassification }
        )
        state.largeClassificationList = { ...state.largeClassificationList, ...newList }
      },
      setMiddleClassificationList(
        state,
        { largeClassificationList }: { largeClassificationList: ChallengeLargeClassification[] }
      ) {
        const newList = largeClassificationList.reduce(
          (acc, classification) =>
            classification.middleCls ? { ...acc, [classification.id]: classification.middleCls } : acc,
          {} as { [id: string]: ChallengeMiddleClassification[] }
        )
        state.middleClassificationList = { ...state.middleClassificationList, ...newList }
      },
      setContestList(
        state,
        {
          largeClassificationId,
          middleClassificationId,
          contestList
        }: { largeClassificationId: string; middleClassificationId: string; contestList: Contest[] }
      ) {
        const newList =
          largeClassificationId in state.contestList
            ? { ...state.contestList[largeClassificationId], [middleClassificationId]: contestList }
            : { [middleClassificationId]: contestList }
        state.contestList = { ...state.contestList, [largeClassificationId]: newList }
      },
      setIsLoading(state, isLoading: boolean) {
        state.isLoading = isLoading
      },
      setError(state, error: string | null) {
        state.error = error
      }
    },
    actions: {
      async getChallengeList({ commit }, { displayLanguage }: { displayLanguage: DisplayLanguageType }) {
        commit('setIsLoading', true)
        commit('setError', null)
        try {
          const res = await getChallengeUseCase.getChallengeList(displayLanguage)
          commit('setLargeClassificationList', { largeClassificationList: res })
          commit('setMiddleClassificationList', { largeClassificationList: res })
        } catch (e) {
          commit('setError', e)
        } finally {
          commit('setIsLoading', false)
        }
      },
      async getChallengeById(
        { commit },
        {
          displayLanguage,
          challengeLargeClassificationId,
          challengeMiddleClassificationId
        }: {
          displayLanguage: DisplayLanguageType
          challengeLargeClassificationId: string
          challengeMiddleClassificationId: string
        }
      ) {
        commit('setIsLoading', true)
        commit('setError', null)
        try {
          const res = await getChallengeUseCase.getChallengeById(
            displayLanguage,
            challengeLargeClassificationId,
            challengeMiddleClassificationId
          )
          commit('setContestList', {
            largeClassificationId: challengeLargeClassificationId,
            middleClassificationId: challengeMiddleClassificationId,
            contestList: res
          })
        } catch (e) {
          commit('setError', e)
        } finally {
          commit('setIsLoading', false)
        }
      }
    },
    getters: {
      largeClassificationList: state => state.largeClassificationList,
      middleClassificationList: state => state.middleClassificationList,
      contestList: state => state.contestList,
      isLoading: state => state.isLoading,
      error: state => state.error
    }
  }
}
